import { z } from 'zod';

const basketSummarySubjectTypeSchema = z.enum(['entity', 'theme']);

export type BasketSummarySubjectType = z.infer<
  typeof basketSummarySubjectTypeSchema
>;
const basketSummaryTypeSchema = z.enum(['earnings', 'monthly', 'daily']);

const basketSummarySubjectSchema = z.object({
  type: basketSummarySubjectTypeSchema,
  id: z.string(),
});

export const basketSummaryFilterRequestSchema = z.object({
  subjects: z.array(basketSummarySubjectSchema).optional(),
  summary_types: z.array(basketSummaryTypeSchema).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  page: z.number().optional(),
  page_size: z.number().optional(),
});

export type BasketSummaryFilterRequest = z.infer<
  typeof basketSummaryFilterRequestSchema
>;

const basketSummaryBaseSchema = z.object({
  id: z.string(),
  subject: basketSummarySubjectSchema,
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});

export const basketSummaryGenerationStatusSchema = z.enum([
  'done',
  'in_progress',
  'error',
  'unknown',
]);

export type BasketSummaryGenerationStatus = z.infer<
  typeof basketSummaryGenerationStatusSchema
>;

const basketSummaryV2BaseSchema = z.object({
  id: z.string().optional(),
  subject: basketSummarySubjectSchema,
  status: basketSummaryGenerationStatusSchema,
  created_at: z.iso.datetime().nullish(),
  updated_at: z.iso.datetime().nullish(),
});

export const basketSummaryCitationSchema = z
  .object({
    id: z.number(),
    entity_tag: z.string(),
    kg_insight_id: z.string().optional(),
    scenario_insight_id: z.string().optional(),
  })
  .refine(
    citation =>
      citation.kg_insight_id !== undefined ||
      citation.scenario_insight_id !== undefined,
    { message: 'kg_insight_id or scenario_insight_id is required' }
  );

export const basketSummaryEarningsCitationSchema =
  basketSummaryCitationSchema.safeExtend({
    earnings_id: z.string(),
    kg_insight_id: z.string(),
  });

export type BasketSummaryEarningsCitation = z.infer<
  typeof basketSummaryEarningsCitationSchema
>;

const basketSummaryDailyCitationSchema = z
  .object({
    id: z.number(),
    kg_insight_id: z.string().optional(),
    scenario_insight_id: z.string().optional(),
  })
  .refine(
    citation =>
      citation.kg_insight_id !== undefined ||
      citation.scenario_insight_id !== undefined,
    { message: 'kg_insight_id or scenario_insight_id is required' }
  );

const basketSummaryContentBaseSchema = z.object({
  summary: z.string(),
  source_ids: z.array(z.string()).optional(),
  scenario_source_ids: z.array(z.string()).optional(),
});

const basketSummaryEarningsContentSchema =
  basketSummaryContentBaseSchema.extend({
    citations: z.array(basketSummaryEarningsCitationSchema),
  });

export type BasketSummaryEarningsContent = z.infer<
  typeof basketSummaryEarningsContentSchema
>;

export const basketSummaryDailyContentSchema =
  basketSummaryContentBaseSchema.extend({
    citations: z.array(basketSummaryDailyCitationSchema),
  });

export type BasketSummaryDailyContent = z.infer<
  typeof basketSummaryDailyContentSchema
>;

const basketSummaryMonthlySentimentSchema = z.object({
  summary: z.string(),
  source_ids: z.array(z.string()).optional(),
  scenario_source_ids: z.array(z.string()).optional(),
  citations: z.array(basketSummaryCitationSchema).optional(),
});

export const basketSummaryMonthlyContentSchema = z.object({
  bearish: basketSummaryMonthlySentimentSchema,
  bullish: basketSummaryMonthlySentimentSchema,
});

export type BasketSummaryMonthlyContent = z.infer<
  typeof basketSummaryMonthlyContentSchema
>;

const basketSummarySchema = z.discriminatedUnion('summary_type', [
  basketSummaryBaseSchema.extend({
    summary_type: z.literal('earnings'),
    content: basketSummaryEarningsContentSchema,
  }),
  basketSummaryBaseSchema.extend({
    summary_type: z.literal('daily'),
    content: basketSummaryDailyContentSchema,
  }),
  basketSummaryBaseSchema.extend({
    summary_type: z.literal('monthly'),
    content: basketSummaryMonthlyContentSchema,
  }),
]);

const basketSummaryV2Schema = z.discriminatedUnion('summary_type', [
  basketSummaryV2BaseSchema.extend({
    summary_type: z.literal('earnings'),
    content: basketSummaryEarningsContentSchema.nullable(),
  }),
  basketSummaryV2BaseSchema.extend({
    summary_type: z.literal('daily'),
    content: basketSummaryDailyContentSchema.nullable(),
  }),
  basketSummaryV2BaseSchema.extend({
    summary_type: z.literal('monthly'),
    content: basketSummaryMonthlyContentSchema.nullable(),
  }),
]);

const basketSummaryFilterResponseSchema = z.array(basketSummarySchema);

export type BasketSummaryFilterResponse = z.infer<
  typeof basketSummaryFilterResponseSchema
>;

const basketSummaryV2FilterResponseSchema = z.array(basketSummaryV2Schema);

export type BasketSummaryV2FilterResponse = z.infer<
  typeof basketSummaryV2FilterResponseSchema
>;

export type BasketSummaryFilterResult =
  | BasketSummaryFilterResponse
  | BasketSummaryV2FilterResponse;

/** One row from either version; v2 rows add `status` and may have null content. */
export type BasketSummaryItem = BasketSummaryFilterResult[number];

export const basketSummary = {
  postBasketSummaryFilter: {
    path: 'basket-summary/v1/filter',
    schema: basketSummaryFilterResponseSchema,
  },
  postBasketSummaryFilterV2: {
    path: 'basket-summary/v2/filter',
    schema: basketSummaryV2FilterResponseSchema,
  },
};
